import Router from "vue-router";
import SignIn from "@/views/application/SignIn.vue";
import SignOut from "@/views/application/SignOut.vue";
import ErrorLogs from "@/views/error-log/ErrorLogs.vue";
import LogViewer from "@/views/log-viewer/LogViewer.vue";
import Users from "@/views/users/Users.vue";
import Vue from "vue";
import store from "./store";
import Info from "@/views/info/Info.vue";
import {UserRoleWithPrefix} from "@/components/users/model/UserRoleWithPrefix";
import Items from "@/views/items/Items";
import Settings from "@/views/settings/Settings";
import ImportItems from "@/views/import/ImportItems";
import ExportItems from "@/views/export/ExportItems";
import ActiveErrors from "@/views/home/panels/ActiveErrors";
import License from "@/views/license/License";
import axios from "axios";
import UserActionResultSender from "@/components/application/UserActionResultSender";

Vue.use(Router);

/**
 * Router definition (URL to component mapping)
 *
 * To use frontend auth, use router meta param:
 * meta: { requiresAuth: ['ADMIN', 'USER'] }
 *
 * To define a loginpage, use:
 * meta: { loginPage: true }
 */
const router = new Router({
  linkExactActiveClass: "current-menu-item",
  routes: [
    {
      path: "/",
      name: "Items",
      component: Items,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
          UserRoleWithPrefix.ROLE_USER,
        ],
      }
    },
    {
      path: "/errors",
      name: "Errors",
      component: ActiveErrors,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_USER,
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
        ],
      }
    },
    {
      path: "/application/signin",
      name: "signIn",
      component: SignIn,
      meta: {
        loginPage: true,
      },
    },
    {
      path: "/application/signout",
      name: "signOut",
      component: SignOut,
    },
    {
      path: "/users",
      name: "users",
      component: Users,
    },
    {
      path: "/settings",
      name: "settings",
      component: Settings,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
        ],
      }
    },
    {
      path: "/items",
      name: "items",
      component: Items,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
          UserRoleWithPrefix.ROLE_USER,
        ],
      }
    },
    {
      path: "/error-logs",
      name: "errorLogs",
      component: ErrorLogs,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_USER,
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
        ],
      }
    },
    {
      path: "/log-viewer",
      name: "logViewer",
      component: LogViewer,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
        ],
      },
    },
    {
      path: "/info",
      name: "info",
      component: Info,
    },
    {
      path: "/import-item",
      name: "ImportItems",
      component: ImportItems,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
          UserRoleWithPrefix.ROLE_USER,
        ],
      }
    },
    {
      path: "/export-item",
      name: "ExportItems",
      component: ExportItems,
      meta: {
        requiresAuth: [
          UserRoleWithPrefix.ROLE_ADMIN,
          UserRoleWithPrefix.ROLE_DEV,
          UserRoleWithPrefix.ROLE_USER,
        ],
      }
    },
    {
      path: "/application/license",
      name: "License",
      component: License
    }
  ],
});

// Before following routes, check license and authentication
router.beforeEach((to, from, next) => {
  const isLoginPage = to.matched.some((record) => record.meta.loginPage);

  const isDevelopmentFeature = to.matched.find(
    (r) => r.meta && r.meta.developmentFeature
  );
  // We disable routing to development features if production profile is active
  if (isDevelopmentFeature && store.state.productionProfileActive) {
    return;
  }

  // Check if meta requiresAuth is set
  const routeRequiresAuth = to.matched.find(
    (r) => r.meta && r.meta.requiresAuth
  );
  let requiresAuth: string[] = [];
  if (routeRequiresAuth) {
    // The found route requires authentication. Fetch meta.RequiresRole from it.
    requiresAuth = routeRequiresAuth.meta.requiresAuth;
  }
  // Retrieve potential user from local storage
  const lsUser = localStorage.getItem("authUser");
  const authUser = lsUser ? JSON.parse(lsUser) : null;
  const isLicensed = localStorage.getItem("isLicensed") === "true";

  if (routeRequiresAuth) {
    // This route requires authorization
    if (!authUser) {
      // There's no user signed in, route to signin page
      next("/application/signIn");
    } else if (
      authUser.authorities.some((r: string) => requiresAuth.includes(r))
    ) {
      // User has the right authorities, continue to follow the route
      next();
    } else {
      // User doesn't have the right authorities, logout the current user to force a new login
      store.dispatch("userSignOut", {error: "auth.error.notAuthorized"});
      router.push("/application/signIn");
    }
  } else if (isLoginPage && authUser) {
    // Already logged in, route to home
    router.push("/");
  } else {
    // No auth required, follow the chosen route
    next();
  }
});

async function isLicenseValid() {
  try {
    const response = await axios.get('/frontend-api/license/check');
    const isLicensed = response.data;
    localStorage.setItem('isLicensed', String(isLicensed));
    return isLicensed;
  } catch (error) {
    UserActionResultSender.showErrorFeedback(error.response.data.responseCode);
    return false;
  }
}


export default router;
